<script setup>
import { computed, ref } from 'vue'
import moment from 'moment'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'
import { trans } from 'laravel-vue-i18n'

// ─── Time helpers ─────────────────────────────────────────────────
const HOUR_PX = 48
const DAY_START = 7
const DAY_END = 20
const HOURS = DAY_END - DAY_START // 13

function parseTime(t) {
    const [h, m] = (t ?? '00:00').split(':').map(Number)
    return h + (m || 0) / 60
}
function fmtTime(t) {
    return t ? t.substring(0, 5).replace(/^0/, '') : ''
}
function eventTop(timeStart) {
    return Math.max(0, parseTime(timeStart) - DAY_START) * HOUR_PX
}
function eventHeight(timeStart, timeEnd) {
    const s = Math.max(0, parseTime(timeStart) - DAY_START)
    const e = Math.min(HOURS, parseTime(timeEnd) - DAY_START)
    return Math.max(20, (e - s) * HOUR_PX)
}

// ─── Color helpers ────────────────────────────────────────────────
function colorWithAlpha(hex, alpha) {
    return hex + Math.round(alpha * 255).toString(16).padStart(2, '0')
}
function eventBg(reservation) {
    const color = getFormColor(reservation.reservation_forms)
    if (reservation.state === 'rejected') return 'transparent'
    if (reservation.state === 'pending') return colorWithAlpha(color, 0.12)
    return color
}
function eventColor(reservation) {
    const color = getFormColor(reservation.reservation_forms)
    if (reservation.state === 'rejected') return '#9BA1AB'
    if (reservation.state === 'pending') return color
    return '#fff'
}
function eventBorder(reservation) {
    const color = getFormColor(reservation.reservation_forms)
    if (reservation.state === 'rejected') return '1px dashed #C9CDD4'
    if (reservation.state === 'pending') return `1px dashed ${color}`
    return 'none'
}

// ─── Props & emits ────────────────────────────────────────────────
const props = defineProps({
    reservations:   { type: Array, default: () => [] },
    visibleFormIds: { type: Set, default: () => new Set() },
    selectedDate:   Object, // moment
})

const emit = defineEmits(['open-reservation', 'hover-reservation', 'reservation-changed'])

// ─── Drag & drop ──────────────────────────────────────────────────
const scrollBodyRef = ref(null)
const gridBodyRef = ref(null)
const dragState = ref(null)
const dropIndicator = ref(null)

function calcDragMinutes(evt) {
    if (!dragState.value || !gridBodyRef.value || !scrollBodyRef.value) return null
    const { offsetY, duration } = dragState.value
    const rect = gridBodyRef.value.getBoundingClientRect()
    const relY = evt.clientY - rect.top + scrollBodyRef.value.scrollTop - offsetY
    const rawHours = DAY_START + Math.max(0, relY) / HOUR_PX
    const snappedMin = Math.round(rawHours * 4) * 15
    const maxStart = DAY_END * 60 - Math.round(duration * 60)
    const startMin = Math.max(DAY_START * 60, Math.min(maxStart, snappedMin))
    const endMin = Math.min(DAY_END * 60, startMin + Math.round(duration * 60))
    return { startMin, endMin }
}

function onDragStart(evt, reservation) {
    const duration = reservation.time_end
        ? parseTime(reservation.time_end) - parseTime(reservation.time_start)
        : 1
    dragState.value = { reservation, offsetY: evt.offsetY, duration }
    evt.dataTransfer.effectAllowed = 'move'
    evt.dataTransfer.setData('text/plain', String(reservation.id))
}

function onDragEnd() {
    dragState.value = null
    dropIndicator.value = null
}

function onColumnDragOver(evt, dateStr) {
    evt.preventDefault()
    const mins = calcDragMinutes(evt)
    if (!mins || !dragState.value) return
    const { startMin, endMin } = mins
    const fmtMin = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
    dropIndicator.value = {
        dateStr,
        top: (startMin / 60 - DAY_START) * HOUR_PX,
        height: Math.max(24, (endMin - startMin) / 60 * HOUR_PX),
        timeLabel: `${fmtMin(startMin)} – ${fmtMin(endMin)}`,
        color: getFormColor(dragState.value.reservation.reservation_forms),
        name: dragState.value.reservation.name,
    }
}

function onColumnDrop(evt, dateStr) {
    evt.preventDefault()
    const mins = calcDragMinutes(evt)
    if (!mins || !dragState.value) return
    const { startMin, endMin } = mins
    const toTime = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}:00`
    emit('reservation-changed', {
        reservation: dragState.value.reservation,
        newDate: dateStr,
        newTimeStart: toTime(startMin),
        newTimeEnd: toTime(endMin),
    })
    dragState.value = null
    dropIndicator.value = null
}

const DOW_ABBR = [
    trans('common.dow_short.mon'),
    trans('common.dow_short.tue'),
    trans('common.dow_short.wed'),
    trans('common.dow_short.thu'),
    trans('common.dow_short.fri'),
    trans('common.dow_short.sat'),
    trans('common.dow_short.sun'),
]

// ─── Computed ─────────────────────────────────────────────────────
const today = new Date()
const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

const weekStart = computed(() => props.selectedDate?.clone().startOf('isoWeek') ?? moment().startOf('isoWeek'))

const weekDays = computed(() => {
    return Array.from({ length: 7 }, (_, i) => {
        const d = weekStart.value.clone().add(i, 'days')
        const dateStr = d.format('YYYY-MM-DD')
        return {
            date: d,
            dateStr,
            dow: i,
            isToday: dateStr === todayStr,
            isWknd: i >= 5,
        }
    })
})

const weekDateStrs = computed(() => weekDays.value.map(d => d.dateStr))

const visibleReservations = computed(() =>
    props.reservations.filter(r => props.visibleFormIds.has(r.id_reservation_forms))
)

function timedEventsForDay(dateStr) {
    return visibleReservations.value.filter(r =>
        r.date === dateStr && r.time_start
    )
}

function allDayEventsForDay(dateStr) {
    return visibleReservations.value.filter(r =>
        r.date_start && r.date_end &&
        dateStr >= r.date_start &&
        dateStr <= r.date_end
    )
}

const hasAnyAllDay = computed(() =>
    weekDays.value.some(d => allDayEventsForDay(d.dateStr).length > 0)
)

const nowTop = computed(() => {
    return (today.getHours() + today.getMinutes() / 60 - DAY_START) * HOUR_PX
})

const nowLabel = computed(() => {
    return `${today.getHours()}:${String(today.getMinutes()).padStart(2, '0')}`
})

const hourMarkers = computed(() => {
    return Array.from({ length: HOURS + 1 }, (_, h) => ({
        h,
        label: `${DAY_START + h}:00`,
        top: h * HOUR_PX,
    }))
})

const todayColIndex = computed(() => weekDays.value.findIndex(d => d.isToday))

function onHoverEnter(reservation, event) {
    emit('hover-reservation', { reservation, x: event.clientX, y: event.clientY })
}
function onHoverLeave() {
    emit('hover-reservation', null)
}
</script>

<template>
    <div class="flex-1 min-h-0 flex flex-col overflow-hidden bg-white dark:bg-neutral-900">

        <!-- Day headers row -->
        <div class="flex flex-shrink-0 border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
            <!-- blank corner above time column -->
            <div class="w-20 flex-shrink-0 border-r border-neutral-100 dark:border-neutral-800"/>
            <!-- 7 day headers -->
            <div
                v-for="d in weekDays"
                :key="d.dateStr"
                class="flex-1 flex flex-col items-center py-2 border-r border-neutral-100 dark:border-neutral-800 last:border-r-0"
                :class="d.isWknd ? 'bg-neutral-50/60 dark:bg-neutral-800/40' : ''"
            >
                <span
                    class="text-[11px] font-semibold uppercase tracking-wider"
                    :class="d.isToday ? 'text-blue-600' : 'text-neutral-400 dark:text-neutral-500'"
                >
                    {{ DOW_ABBR[d.dow] }}
                </span>
                <div
                    class="w-7 h-7 flex items-center justify-center rounded-full text-[14px] font-semibold mt-0.5"
                    :class="d.isToday ? 'bg-blue-600 text-white' : 'text-neutral-700 dark:text-neutral-300'"
                >
                    {{ d.date.date() }}
                </div>
            </div>
        </div>

        <!-- All-day strip -->
        <div v-if="hasAnyAllDay" class="flex flex-shrink-0 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/40">
            <div class="w-20 flex-shrink-0 border-r border-neutral-100 dark:border-neutral-800 text-[10px] text-neutral-400 dark:text-neutral-500 flex items-center justify-center">
                {{ $t('reservations.cal_all_day') }}
            </div>
            <div
                v-for="d in weekDays"
                :key="d.dateStr"
                class="flex-1 flex flex-col gap-px p-1 min-h-[28px] border-r border-neutral-100 dark:border-neutral-800 last:border-r-0"
                :class="d.isWknd ? 'bg-neutral-50/60 dark:bg-neutral-800/40' : ''"
            >
                <div
                    v-for="r in allDayEventsForDay(d.dateStr)"
                    :key="r.id"
                    class="px-1.5 h-5 rounded text-[10px] font-medium cursor-pointer hover:opacity-80 transition-opacity truncate flex items-center"
                    :style="{
                        background: eventBg(r),
                        color: eventColor(r),
                        border: eventBorder(r),
                    }"
                    @click="emit('open-reservation', r)"
                >
                    {{ r.name }}
                </div>
            </div>
        </div>

        <!-- Body: time col + 7-column grid -->
        <div ref="scrollBodyRef" class="flex-1 overflow-y-auto flex min-h-0">

            <!-- Time column -->
            <div
                class="w-20 flex-shrink-0 border-r border-neutral-100 dark:border-neutral-800 relative"
                :style="{ height: HOURS * HOUR_PX + 'px' }"
            >
                <div
                    v-for="({ label, top }, i) in hourMarkers"
                    :key="i"
                    class="absolute right-3 text-[10px] text-neutral-400 dark:text-neutral-600 select-none"
                    :style="{ top: (i === 0 ? 0 : top - 7) + 'px' }"
                >
                    {{ label }}
                </div>
            </div>

            <!-- 7 columns area -->
            <div ref="gridBodyRef" class="flex-1 relative" :style="{ height: HOURS * HOUR_PX + 'px' }">

                <!-- Vertical day dividers -->
                <div
                    v-for="i in 6"
                    :key="i"
                    class="absolute top-0 bottom-0 w-px bg-neutral-100 dark:bg-neutral-800"
                    :style="{ left: (i * 100 / 7) + '%' }"
                />

                <!-- Hour lines -->
                <div
                    v-for="h in HOURS"
                    :key="h"
                    class="absolute left-0 right-0 bg-neutral-100 dark:bg-neutral-800"
                    style="height: 1px"
                    :style="{ top: h * HOUR_PX + 'px' }"
                />

                <!-- Now indicator: only on today's column -->
                <div
                    v-if="todayColIndex >= 0"
                    class="absolute z-10 pointer-events-none"
                    :style="{
                        top: nowTop + 'px',
                        left: (todayColIndex * 100 / 7) + '%',
                        width: (100 / 7) + '%',
                    }"
                >
                    <div class="h-[2px] bg-red-500 relative">
                        <div class="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-red-500"/>
                        <span class="absolute left-3 -top-[9px] text-[10px] text-red-500 font-semibold bg-white dark:bg-neutral-900 px-0.5">
                            {{ nowLabel }}
                        </span>
                    </div>
                </div>

                <!-- Per-day event columns -->
                <div
                    v-for="(d, di) in weekDays"
                    :key="d.dateStr"
                    class="absolute top-0 bottom-0"
                    :style="{
                        left: (di * 100 / 7) + '%',
                        width: (100 / 7) + '%',
                    }"
                    @dragover="onColumnDragOver($event, d.dateStr)"
                    @drop="onColumnDrop($event, d.dateStr)"
                >
                    <!-- Drop indicator -->
                    <div
                        v-if="dropIndicator?.dateStr === d.dateStr"
                        class="absolute rounded-[6px] pointer-events-none z-20 flex flex-col px-2 py-1 overflow-hidden"
                        :style="{
                            top: dropIndicator.top + 'px',
                            height: dropIndicator.height + 'px',
                            left: '2px',
                            right: '4px',
                            background: dropIndicator.color + '28',
                            border: `2px dashed ${dropIndicator.color}`,
                        }"
                    >
                        <div class="text-[10px] font-bold leading-none mb-0.5" :style="{ color: dropIndicator.color }">
                            {{ dropIndicator.timeLabel }}
                        </div>
                        <div class="text-[11px] font-medium leading-tight truncate" :style="{ color: dropIndicator.color }">
                            {{ dropIndicator.name }}
                        </div>
                    </div>

                    <div
                        v-for="r in timedEventsForDay(d.dateStr)"
                        :key="r.id"
                        draggable="true"
                        class="absolute cursor-pointer rounded-[6px] px-2 py-1 transition-shadow hover:shadow-md overflow-hidden"
                        @dragstart="onDragStart($event, r)"
                        @dragend="onDragEnd"
                        :style="{
                            top: eventTop(r.time_start) + 'px',
                            height: eventHeight(r.time_start, r.time_end) + 'px',
                            left: '2px',
                            right: '4px',
                            background: eventBg(r),
                            color: eventColor(r),
                            border: eventBorder(r),
                        }"
                        @click="emit('open-reservation', r)"
                        @mouseenter="onHoverEnter(r, $event)"
                        @mouseleave="onHoverLeave"
                    >
                        <div class="text-[10px] font-semibold leading-none mb-0.5" style="opacity: .9">
                            {{ fmtTime(r.time_start) }}
                        </div>
                        <div class="text-[11px] font-medium leading-tight truncate">{{ r.name }}</div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>
